import { useMemo } from 'react';

import { type Address, type Nullable } from '~/common/types';
import { type RouterOutputs, trpc } from '@/config/trpc';

import CaseStatus from '@/shared/enums/CaseStatus';
import useDebounce from './UseDebounce';

const DEBOUNCE_DELAY_MS = 1000;

const DUPLICATE_CASE_STATUSES = [CaseStatus.Cancelled, CaseStatus.Open, CaseStatus.Pending];

export type DuplicateAddressCase = RouterOutputs['case']['list']['items'][number];

type UseDuplicateAddressCasesParams = {
  address: Partial<Address>;
  enabled?: boolean;
  excludeCaseId?: Nullable<string>;
};

/**
 * Looks up existing cases sharing the given address, to warn about accidental duplicates.
 *
 * The address parts are debounced internally, so consumers can pass raw (undebounced) form values.
 * The lookup only runs once both `addressLine` and `postalCode` are filled in.
 *
 * @param params.address - Address to look for. Only `addressLine` and `postalCode` are required.
 * @param params.enabled - Set to false to suspend the lookup entirely. Defaults to true.
 * @param params.excludeCaseId - Case to leave out of the results, typically the case being edited.
 * @returns The matching cases and whether a lookup is in flight.
 */
const useDuplicateAddressCases = ({
  address,
  enabled = true,
  excludeCaseId,
}: UseDuplicateAddressCasesParams) => {
  const addressLine = useDebounce(address.addressLine, DEBOUNCE_DELAY_MS);
  const city = useDebounce(address.city, DEBOUNCE_DELAY_MS);
  const country = useDebounce(address.country, DEBOUNCE_DELAY_MS);
  const postalCode = useDebounce(address.postalCode, DEBOUNCE_DELAY_MS);

  const { data, isFetching } = trpc.case.list.useQuery(
    {
      params: {
        addressLine: addressLine ?? undefined,
        city: city ?? undefined,
        country: country ?? undefined,
        postalCode: postalCode ?? undefined,
        statuses: DUPLICATE_CASE_STATUSES,
      },
    },
    {
      enabled: enabled && !!addressLine && !!postalCode,
    },
  );

  const cases = useMemo(
    () => (data?.items ?? []).filter((duplicate) => duplicate.id !== excludeCaseId),
    [data, excludeCaseId],
  );

  return { cases, isFetching };
};

export default useDuplicateAddressCases;
